import type {ReactElement, RefObject} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {SocialMediaWizardNav} from '@Components/social-media-wizard/components/social-media-wizard-nav';
import {SocialMediaWizardContent} from '@Components/social-media-wizard/components/social-media-wizard-content';
import {SocialAccountsList} from '@Components/social-media-wizard/components/social-accounts-list';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {DesignMetaUpdateInfo} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  getWizardContentSize,
  PUBLISH_PARAM_HANDLER_CLASS,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialAccountConnection} from '@Components/social-media-wizard/components/social-account-connection';
import {SocialAccountButtons} from '@Components/social-media-wizard/components/social-account-buttons';
import {
  useAccountIdsToPublishTo,
  useAccountTypeToConnect,
  useHaveAllPostsPublished,
  useNextButtonText,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useTriggerNext,
  useTriggerPrevious,
  useUpdatePublishingParams,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {
  getGraphicMeta,
  getLoadedTextByPublishingAction,
  getLoadingTextByPublishingAction,
  getNextWizardStep,
  getStartingURIForGallery,
} from '@Libraries/social-media-wizard-library';
import {DesignSelector} from '@Components/design-selector';
import {
  disableNextButton,
  enableNextButton,
  hideAccountLevelErrorMessage,
  initPublishingParams,
  resetAccountResizeStatusForDesignsMap,
  resetSocialPostCoverForAllPublishingParams,
  showNextButton,
  syncEditedDesign,
  syncPublishingParams,
  updateAccountIdsToPublishTo,
  updateAccountLevelErrorMessage,
  updateAccountNeedsResizeMap,
  updateAccountTypeToConnect,
  updateDesignsToPublish,
  updateDesignsToPublishAndTriggerNext,
  updateDesignSelectorTab,
  updateDesignsGraphicMetaMap,
  updateEmphasizedPlatform,
  updateHasSeenConfetti,
  updateHasSeenOnboarding,
  updateHasUserPublishedBefore,
  updateIsGoogleBusinessProfileEnabled,
  updateIsSocialMediaWizardAiCaptionsEnabled,
  updateIsTemplateSelected,
  updateNextButtonIcon,
  updateNextButtonText,
  updateNumParentAccountsConnected,
  updatePost,
  updatePublishingAction,
  updatePublishingParams,
  updateTriggerNext,
  updateTriggerPrevious,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {SocialMediaPostDetails} from '@Components/social-media-wizard/components/social-media-post-details';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {
  getPostId,
  useDesignSelectorTab,
  useDesignsToPublish,
  useIsTemplateSelected,
  useSocialMediaWizardContainerQuery,
  useWizardCaptionErrors,
  useWizardConnectedAccounts,
  useWizardDesignsGraphicMetaMap,
  useWizardMobileNavView,
  useWizardPublishingParams,
  useWizardTitleErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {SocialMediaMobileFooter} from '@Components/social-media-wizard/components/social-media-mobile-footer';
import {EmbeddedEditorAction, openDesignInEmbeddedEditor} from '@Libraries/embedded-editor';
import type {GraphicMeta, GraphicsMetaMapType, PageGraphicsMetaMap, PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import {MultiPostDesignActionPurposes, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {DesignSelection, DesignsMetaForPublishing, SocialPlatform} from '@Components/social-media/social-media.types';
import {getPosterScreenPreviewURL, getPostersPageIds, pollForPosterPreviewGenerationStatus, pollForUpdatesInURL} from '@Libraries/poster-library';
import {savePreferredProfileSelectionUser} from '@Components/social-media/social-media-thunk';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import {addClickOutsideListener} from '@Utils/dom.util';
import useHasSeenAiCaptionIntroPopup from '@Hooks/auto-captions/useHasSeenAiCaptionIntroPopup';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {SocialMediaWizardOnboarding} from '@Components/social-media-wizard/components/social-media-wizard-onboarding';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {DesignGallery} from '@Components/design-gallery';
import {TemplateEditorPanel} from '@Components/social-media-wizard/components/template-editor-panel';
import {SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {SocialPostContent} from '@Components/social-media/components/social-post-content-multiposting';
import type {SocialPostsDesignActionModalProps} from '@Components/modals/social-post-design-action-modal';
import {openDesignActionModal} from '@Components/modals/social-post-design-action-modal';
import {getMobilePreviewWindowHeight} from '@Components/social-media-wizard/social-media-wizard-library';
import {SocialMediaWizardDesignSelectorPreview} from '@Components/social-media/components/social-media-wizard-design-selector-preview';
import {asyncnoop, noop} from '@Utils/general.util';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {GA4EventName} from '@Libraries/ga-events';
import {openErrorModal} from '@Modals/error-modal';
import {requestResetAiCaptionInsights} from '@Libraries/auto-caption-library';
import {
  areAllDesignPagesSupportedForAccounts,
  CACHE_KEY_FOR_SCHEDULING,
  convertDateToUTCTimestamp,
  doesVideoExist,
  getCaptionErrorAccountId,
  getTitleErrorAccountId,
  goBackToEvent,
  goBackToEventFromOneClickPromotion,
  isEventOneClickPromotionFlow,
  isEventsSocialPostingFlow,
} from '@/libraries/social-media';
import styles from './social-media-wizard.module.scss';
import {useAppDispatch} from '@/hooks';
import type {SocialMediaAccountId, SocialPost} from '../social-media/post.vo';
import {PostState} from '../social-media/post.vo';
import '@Global/modal';
import {openSocialMediaAddDesignModal} from '@Modals/social-media-add-design-modal/social-media-add-design-modal';
import {openDesignSelectorModal} from '@Modals/design-selector-modal/design-selector-modal';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 900,
  },
  large: {
    minWidth: 901,
    maxWidth: 1149,
  },
  xlarge: {
    minWidth: 1150,
  },
};

interface SocialMediaWizardProps {
  post?: SocialPost;
  accounts?: SocialAccount[];
  accountIdsForPost?: number[];
  design?: DesignSelection;
  template?: DesignSelection;
  isGalleryFlow?: boolean;
  eventHashId?: string;
  numParentAccountsConnected?: number;
  hasSeenOnboarding?: boolean;
  hasSeenConfetti?: boolean;
  hasUserPublishedBefore?: boolean;
  isSocialMediaWizardAiCaptionsEnabled?: boolean;
  isGoogleBusinessProfileEnabled?: boolean;
  emphasizedPlatform?: SocialPlatform;
}

interface PopStateEventWithStep extends PopStateEvent {
  state: {
    step: SocialMediaWizardStep;
  };
}

export function SocialMediaWizard({
  post,
  accounts = [],
  accountIdsForPost = [],
  design,
  template,
  isGalleryFlow = false,
  eventHashId,
  numParentAccountsConnected = 0,
  hasSeenOnboarding = false,
  hasSeenConfetti = false,
  hasUserPublishedBefore = false,
  isSocialMediaWizardAiCaptionsEnabled = false,
  isGoogleBusinessProfileEnabled = false,
  emphasizedPlatform,
}: SocialMediaWizardProps): ReactElement<any> {
  const accountTypeToConnect = useAccountTypeToConnect();
  const wizardStep = useWizardStep();
  const triggerNext = useTriggerNext();
  const nextButtonText = useNextButtonText();
  const wizardPost = useSocialMediaWizardPost();
  const isTemplateSelected = useIsTemplateSelected();
  const isSelectTemplateFlow = !isUserLoggedIn() || isTemplateSelected;
  const navView = useWizardMobileNavView();
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const publishingAction = useSocialPostPublishingAction();
  const prevStepRef = useRef<SocialMediaWizardStep | null>(wizardStep);
  const triggerPrevious = useTriggerPrevious();
  const dispatch = useAppDispatch();

  const [previousSteps, setPreviousSteps] = useState<SocialMediaWizardStep[]>([]);
  const [isLoadingEmbeddedEditor, setIsLoadingEmbeddedEditor] = useState(false);
  const [isDesignSelectorLoading, setDesignSelectorIsLoading] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [areAllPreviewsUpToDate, setAreAllPreviewsUpToDate] = useState(false);
  const [designsToPublish, setDesignsToPublish] = useState<DesignSelection[]>([]);

  let isFluidContent = false;
  let mainContent = <SocialAccountButtons />;
  let header = '';

  const {containerQueryParamProps, containerRef, hasContainerQueryInitialized} = useSocialMediaWizardContainerQuery();
  const myRef: RefObject<HTMLElement | null> = containerRef as RefObject<HTMLElement | null>;
  const params = useContainerQuery(myRef, containerQuery);
  const previewParentRef = useRef<HTMLDivElement>(null);
  const designSelectorRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const connectedAccounts = useWizardConnectedAccounts();
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(containerRef as RefObject<HTMLElement | null>);
  const [bottomShadowSelector, setBottomShadowSelector] = useState(false);
  const designsToPublishTo = useDesignsToPublish();
  const postPublishingParams = useWizardPublishingParams();
  const designsGraphicsMetaMap = useWizardDesignsGraphicMetaMap();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const socialPost = useSocialMediaWizardPost();
  const {setInAutoCaptionMode} = useAutoCaptionGlobal();
  const {fetchHasSeenStatus} = useHasSeenAiCaptionIntroPopup();
  const postId = getPostId();
  const designSelectorTab = useDesignSelectorTab();
  const isAiCaptionInsightsResetDone = useRef<boolean>(false);
  const MAX_UPLOADS_TO_DESIGNS = 10;
  const NOON_HOUR = 12;
  const ONE_HOUR_OFFSET = 1;
  const onUploadMediaDesignsCallbackRef = useRef((_: DesignSelection[]) => {});
  const connectedAccountIds = connectedAccounts.map((account) => {
    return account.id;
  });
  const haveAllPostsPublished = useHaveAllPostsPublished();

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    void fetchHasSeenStatus();
  }, []);

  useEffect(() => {
    const removeListener = addClickOutsideListener(`.social-caption-container.-in-ai-mode`, onOutsideAiCaptionPromptBoxClick);

    return (): void => {
      removeListener();
    };
  }, []);

  useEffect(() => {
    if (!isUserPremium() || !connectedAccountIds.length || isAiCaptionInsightsResetDone.current || !postId) {
      return;
    }

    void requestResetAiCaptionInsights(postId, connectedAccountIds);
    isAiCaptionInsightsResetDone.current = true;
  }, [connectedAccountIds, postId]);

  const onOutsideAiCaptionPromptBoxClick = (): void => {
    setInAutoCaptionMode({accountId: null});
  };

  const setWizardStep = (step: SocialMediaWizardStep | null): void => {
    if (step) {
      dispatch(updateWizardStep(step));
    }
  };

  const isSchedulingDateSameAsCurrentDate = (schedulingDate: Date): boolean => {
    const currentDate = new Date();
    return currentDate.getFullYear() === schedulingDate.getFullYear() && currentDate.getMonth() === schedulingDate.getMonth() && currentDate.getDate() === schedulingDate.getDate();
  };

  const setSchedulingDateForNoon = (schedulingDate: Date): void => {
    schedulingDate.setHours(NOON_HOUR, 0, 0, 0);
  };

  const setSchedulingDateForNextHour = (schedulingDate: Date): void => {
    const currentDate = new Date();
    const MIN_SCHEDULING_TIME_GAP_MINS = 15;
    const ONE_HR_MINS = 60;

    const currentMinutes = currentDate.getMinutes();
    const currentHours = currentDate.getHours();

    const minutesToNextHour = ONE_HR_MINS - currentMinutes;
    const isTimeToNextHrLessThanMinSchedulingGap = minutesToNextHour < MIN_SCHEDULING_TIME_GAP_MINS;

    if (isTimeToNextHrLessThanMinSchedulingGap) {
      schedulingDate.setHours(currentHours, currentMinutes + MIN_SCHEDULING_TIME_GAP_MINS, 0, 0);
    } else {
      schedulingDate.setHours(currentHours + ONE_HOUR_OFFSET, 0, 0, 0);
    }
  };

  const getDateObjectForPrefilledDate = (prefilledDate: string): Date => {
    const schedulingDate = new Date(prefilledDate);

    if (isSchedulingDateSameAsCurrentDate(schedulingDate)) {
      setSchedulingDateForNextHour(schedulingDate);
    } else {
      setSchedulingDateForNoon(schedulingDate);
    }

    return schedulingDate;
  };

  const setDetailsForSchedulingIfNeeded = (): void => {
    const prefilledDate = window.PMW.getCacheItem(CACHE_KEY_FOR_SCHEDULING) as string;

    if (!prefilledDate) {
      return;
    }

    const dateTime = getDateObjectForPrefilledDate(prefilledDate);
    const timeZoneOffset = dateTime.getTimezoneOffset();

    if (socialPost !== undefined) {
      const newPost: SocialPost = {
        ...socialPost,
        state: PostState.SCHEDULE_READY,
        publishOn: convertDateToUTCTimestamp(dateTime),
        timeZoneOffset,
      };

      dispatch(updatePost(newPost));
    }
  };

  useDidUpdateEffect(() => {
    if (designsToPublishTo.length === 0 && post) {
      setDesignsToPublish(post.designs);
      dispatch(updateDesignsToPublish(post.designs));

      return;
    }

    if (isUserLoggedIn() && connectedAccountIds.length > 0) {
      void updatePublishingDesignsMeta();
    } else if (template || designsToPublishTo.length > 0) {
      void fetchGraphicsMetaForAnonymousTemplates(designsToPublishTo[0] ?? template);
    }
  }, [designsToPublishTo, connectedAccountIds.length]);

  const resetResizeStatesOnClosingEmbeddedEditor = (): void => {
    dispatch(resetAccountResizeStatusForDesignsMap());
  };

  const onEmbeddedEditorClosed = async (posterId: PosterHashedId): Promise<void> => {
    await updatePublishingDesignsMeta(design);

    const updatedEntries = Object.entries(designsGraphicsMetaMap[posterId]).map(([pageHashId, graphicMeta]) => {
      const newGraphicMeta: GraphicMeta = {
        ...graphicMeta,
        graphicURL: '',
        isVideo: false,
      };

      return [pageHashId, newGraphicMeta];
    });

    const loadingGraphicsPageMetas = Object.fromEntries(updatedEntries) as PageGraphicsMetaMap;

    const newGraphicsMetaMap: GraphicsMetaMapType = {
      ...designsGraphicsMetaMap,
      [posterId]: loadingGraphicsPageMetas,
    };

    dispatch(updateDesignsGraphicMetaMap(newGraphicsMetaMap));

    setIsPreviewLoading(true);

    const onPreviewGenerationSuccess = (): void => {
      resetResizeStatesOnClosingEmbeddedEditor();
      void updateDesignsMetaOnClosingEmbeddedEditor().finally(() => {
        setIsPreviewLoading(false);
      });
    };

    const onPreviewGenerationFailure = (err: unknown): void => {
      console.error(err);
      setIsPreviewLoading(false);
    };

    pollForPosterPreviewGenerationStatus(posterId, onPreviewGenerationSuccess, onPreviewGenerationFailure);
  };

  const onEditDesignSelectionCallback = (design: DesignSelection): void => {
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(design.posterHashedId, onEmbeddedEditorOpened, () => {
      void onEmbeddedEditorClosed(design.posterHashedId);
    });
  };

  const isSingleDesignSelected = (): boolean => {
    return designsToPublishTo.length === 1;
  };

  const onClickEditDesign = (): void => {
    triggerEditDesignsFlow();
  };

  const triggerEditDesignsFlow = (): void => {
    if (isSingleDesignSelected()) {
      onEditDesignSelectionCallback(designsToPublishTo[0]);
      return;
    }

    const designActionModal: SocialPostsDesignActionModalProps = {
      designsToPublish: designsToPublishTo,
      designsGraphicsMetaMap,
      designActionCallback: onEditDesignSelectionCallback,
      actionPurpose: MultiPostDesignActionPurposes.EDIT,
      showRemoveDesignCta: true,
      removeDesignCallback: (designs: DesignSelection[]): void => {
        setIsPreviewLoading(true);
        dispatch(updateDesignsToPublish(designs));
      },
    };

    openDesignActionModal(designActionModal);
  };

  const addDesignModalMyUploadsSelectionCallback = (): void => {
    window.PMW.triggerCreateDesignFromUserUpload({
      allowMultipagePosterCreation: false,
      maxSelectionNumber: MAX_UPLOADS_TO_DESIGNS,
      // The wizard publishes the created posters right away, so it needs fully processed media.
      shouldUseFastPendingImageUploadFlow: false,
      onPosterCreated: noop,
      onAllPostersCreated: (posterHashedIds: PosterHashedId[]): void => {
        getPostersPageIds(posterHashedIds)
          .then((pageIdsMap: Record<PosterHashedId, PageHashedId[]>) => {
            const newDesignSelections = Object.keys(pageIdsMap).flatMap((posterId): DesignSelection[] => {
              return pageIdsMap[posterId].map((pageId): DesignSelection => {
                return {posterHashedId: posterId, pageHashedId: pageId};
              });
            });

            dispatch(updateDesignsToPublish([...designsToPublishTo, ...getFilteredDesigns(newDesignSelections)]));
            dispatch(enableNextButton());
          })
          .catch((err: unknown) => {
            console.error('Error fetching poster page IDs:', err);
          });
      },
      onPosterCreationsStarted: (): void => {
        setIsPreviewLoading(true);
        dispatch(disableNextButton());
      },
      footerPrimaryCtaText: window.i18next.t('pmwjs_add_selected'),
      hideFooterSecondaryActions: true,
    });
  };

  const addDesignModalMyDesignsSelectionCallback = (): void => {
    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.MY_DESIGNS, DesignSelectorTab.SHARED_WITH_ME],
      onAddSelected: (selectedDesigns: DesignSelection[]) => {
        setIsPreviewLoading(true);
        setDesignsToPublish(selectedDesigns);

        dispatch(updateDesignsToPublish(selectedDesigns));
      },
      selectedDesigns: designsToPublishTo,
    });
  };

  const onAddDesignGalleryTemplateSelected = (templateSelected: DesignSelection): void => {
    setIsPreviewLoading(true);
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(
      templateSelected.posterHashedId,
      onEmbeddedEditorOpened,
      (newDesignId: string) => {
        getPostersPageIds([newDesignId])
          .then((pageIdsMap: Record<PosterHashedId, PageHashedId[]>) => {
            if (!pageIdsMap[newDesignId]) {
              console.error('No page IDs found for poster:', newDesignId);
              return;
            }

            const newDesignSelections = pageIdsMap[newDesignId].map((pageId): DesignSelection => {
              return {posterHashedId: newDesignId, pageHashedId: pageId};
            });

            dispatch(updateDesignsToPublish([...designsToPublishTo, ...newDesignSelections]));
          })
          .catch((err: unknown) => {
            console.error('Error fetching poster page IDs:', err);
          });
      },
      () => {
        setIsPreviewLoading(false);
      },
      EmbeddedEditorAction.COPY
    );
  };

  const addDesignModalCreateDesignSelectionCallback = (): void => {
    const designGalleryHeight = '88vh';

    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.GALLERY_TEMPLATES],
      selectedDesigns: designsToPublishTo,
      designGalleryStartingURI: getStartingURIForGallery(),
      onDesignSelectionCallback: onAddDesignGalleryTemplateSelected,
      designGalleryHeight: designGalleryHeight,
    });
  };

  const onAddDesignClick = (): void => {
    openSocialMediaAddDesignModal({
      myUploadsSelectionCallback: addDesignModalMyUploadsSelectionCallback,
      myDesignsSelectionCallback: addDesignModalMyDesignsSelectionCallback,
      createDesignSelectionCallback: addDesignModalCreateDesignSelectionCallback,
    });
  };

  const getAddDesignCta = (): ReactElement<any> => {
    return (
      <Button
        type={Type.SECONDARY}
        icon="icon icon-plus"
        size={Size.SMALL}
        customClasses={`${styles.buttonShadow} ${styles.editDesignButton} spacing-m-r-2`}
        text={window.i18next.t('pmwjs_add_design')}
        iconPlacement={IconPlacement.LEFT}
        onClick={onAddDesignClick}
      />
    );
  };

  const getEditDesignCta = (): ReactElement<any> => {
    return (
      <Button
        type={Type.SECONDARY}
        icon="icon icon-pencil"
        size={Size.SMALL}
        customClasses={`${styles.buttonShadow} ${styles.editDesignButton}`}
        text={window.i18next.t('pmwjs_edit_design_capitalized')}
        iconPlacement={IconPlacement.LEFT}
        onClick={onClickEditDesign}
        isLoading={isLoadingEmbeddedEditor}
      />
    );
  };

  const getPreviewContentCta = (): ReactElement<any> | null => {
    if (isPreviewLoading || isSelectTemplateFlow) {
      return null;
    }

    return (
      <div className="flex-h-row">
        {getAddDesignCta()}
        {getEditDesignCta()}
      </div>
    );
  };

  const onLoadCallback = (): void => {
    setIsPreviewLoading(false);
  };

  const getPreviewStyles = (): NonNullable<unknown> | undefined => {
    if (!isMobileView()) {
      return undefined;
    }

    return {
      height: `${getMobilePreviewWindowHeight()}px`,
    };
  };

  const onOrphanedPreviewContainerClick = (): void => {
    if (isSelectTemplateFlow || isPreviewLoading) {
      return;
    }

    triggerEditDesignsFlow();
  };

  const getSocialPostContent = (): ReactElement<any> | null => {
    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS && !areAllPreviewsUpToDate) {
      return null;
    }

    return (
      <SocialPostContent
        designsToPublish={designsToPublishTo}
        onLoadCallback={onLoadCallback}
        areDetailsLoading={false}
        parentContainerRef={previewParentRef}
        type={SocialPostContentType.ORPHANED_PREVIEW}
        onOrphanedPreviewContainerClick={onOrphanedPreviewContainerClick}
        accountsToPublishTo={[]}
        publishingParams={postPublishingParams}
        designsGraphicMetaMap={designsGraphicsMetaMap}
      />
    );
  };

  const getPreviewContentWrapperClasses = (): string => {
    return `${styles.previewWrapper} ${!getPreviewContentCta() ? styles.anonTemplateWrapper : ''} ${wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ? styles.selectAccountsStepWrapper : ''} `;
  };

  const getPreviewContent = (): ReactElement<any> | undefined => {
    const isLoading = wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ? isPreviewLoading && !areAllPreviewsUpToDate : isPreviewLoading;

    return (
      <div ref={previewParentRef} style={getPreviewStyles()} className={getPreviewContentWrapperClasses()}>
        <CircularProgressLoader classes={isLoading ? '_position-absolute' : '_hidden'} size={LOADER_SIZE.MEDIUM} />
        <div className={`_full-height _full-width ${isLoading ? '_invisible' : ''}`}>{getSocialPostContent()}</div>
        {getPreviewContentCta()}
      </div>
    );
  };

  const isMobileView = (): boolean => {
    return params.small || params.xsmall;
  };

  let previewContent: ReactElement<any> | undefined = <>{getPreviewContent()}</>;

  const setDesignsMeta = (previewUrls: string[], designIsVideo: boolean[], designNames: string[], designSelectionInformation: DesignSelection[] = []): void => {
    const graphicsMetaMap: GraphicsMetaMapType = {};

    for (let i = 0; i < designSelectionInformation.length; i++) {
      const {posterHashedId, pageHashedId} = designSelectionInformation[i];
      const previewURL = previewUrls[i];
      const isVideo = designIsVideo[i];
      const name = designNames[i];

      graphicsMetaMap[posterHashedId] ??= {};
      graphicsMetaMap[posterHashedId][pageHashedId] = {
        isVideo,
        graphicURL: previewURL,
        name,
        order: i,
      };
    }

    dispatch(updateDesignsGraphicMetaMap(graphicsMetaMap));
  };

  const onEmbeddedEditorOpened = (): void => {
    setIsLoadingEmbeddedEditor(false);
  };

  const linkSocialPostToEvent = (): void => {
    const idPost = wizardPost ? wizardPost.id : null;
    if (!eventHashId) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/linkSocialPostToEvent'), {
      idPost,
      eventHashId,
    });
  };

  const updateDesignsMetaOnClosingEmbeddedEditor = async (): Promise<void> => {
    const wizardPostId = wizardPost ? wizardPost.id : null;
    const data = (await window.PMW.writeLocal('socialmedia/updateDesignSelectionMetaForPublishing', {
      designSelections: JSON.stringify(designsToPublishTo),
      postId: wizardPostId,
    })) as DesignsMetaForPublishing;

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMap) {
      dispatch(updateAccountNeedsResizeMap(data.accountNeedsResizeMap));
    }

    setDesignsMeta(data.previewUrls, data.designsIsVideo, data.designNames, designsToPublishTo);
  };

  const updatePublishingDesignsMeta = async (freshDesign?: DesignSelection): Promise<DesignMetaUpdateInfo> => {
    if (designsToPublishTo.length === 0) {
      return {hasAnyVideo: false, areAllAccountsSupported: true};
    }

    const designsToUpdate = freshDesign ? [freshDesign] : designsToPublishTo;
    const wizardPostId = wizardPost ? wizardPost.id : null;

    const data = (await window.PMW.writeLocal('socialmedia/updateDesignSelectionMetaForPublishing', {
      designSelections: JSON.stringify(designsToUpdate),
      postId: wizardPostId,
    })) as DesignsMetaForPublishing;

    const designMetaUpdateInfo = {
      hasAnyVideo: doesVideoExist(data.designsIsVideo),
      areAllAccountsSupported: true,
    };

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMap) {
      const resizeMap = data.accountNeedsResizeMap;
      designMetaUpdateInfo.areAllAccountsSupported = areAllDesignPagesSupportedForAccounts(resizeMap, accountIdsToPublishTo);
      dispatch(updateAccountNeedsResizeMap(resizeMap));
    }

    setDesignsMeta(data.previewUrls, data.designsIsVideo, data.designNames, designsToUpdate);

    return designMetaUpdateInfo;
  };

  const shouldShowScheduleDetails = (): boolean => {
    if (!wizardPost?.state) {
      return false;
    }

    if (
      wizardPost.state === PostState.SCHEDULED ||
      wizardPost.state === PostState.PUBLISHING ||
      wizardPost.state === PostState.DRAFT ||
      wizardPost.state === PostState.ERROR ||
      wizardPost.state === PostState.SCHEDULE_ERROR
    ) {
      return false;
    }

    return !!(wizardPost?.publishOn && wizardPost.timeZoneOffset !== null && wizardStep === SocialMediaWizardStep.ADD_DETAILS);
  };

  const isAccountRelatedStep = (step: SocialMediaWizardStep): boolean => {
    return step === SocialMediaWizardStep.ACCOUNT_CONNECTION || step === SocialMediaWizardStep.SELECT_ACCOUNTS || step === SocialMediaWizardStep.CONNECT_ACCOUNTS;
  };

  const doUpdateNextButtonIfNeeded = (step: SocialMediaWizardStep): void => {
    if (isSelectTemplateFlow && isAccountRelatedStep(step)) {
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_next_edit_template')));
      dispatch(updateNextButtonIcon('icon-arrow-right'));
    } else if (step === SocialMediaWizardStep.ADD_DETAILS) {
      if (shouldShowScheduleDetails()) {
        dispatch(updateNextButtonText(window.i18next.t('pmwjs_schedule')));
        dispatch(updateNextButtonIcon('icon-calendar'));
        return;
      }

      dispatch(updateNextButtonText(window.i18next.t('pmwjs_publish')));
      dispatch(updateNextButtonIcon('icon-publish-arrow'));
    } else if (nextButtonText !== window.i18next.t('pmwjs_next')) {
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_next')));
      dispatch(updateNextButtonIcon('icon-arrow-right'));
    }
  };

  const onNext: VoidFunction = (): void => {
    initPanelSpecificHandlersOnNext();
    const nextStep = getNextWizardStep(post, accounts, wizardStep, {
      isGalleryFlow: isSelectTemplateFlow,
      emphasizedPlatform,
      template,
    });

    hideMobilePageSelectorPopup();

    if (nextStep) {
      initPanelSpecificHandlersOnCurrentStep(nextStep);

      const isAnyAccountErrorProne = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) !== null || getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null;

      if (isAnyAccountErrorProne && nextStep === SocialMediaWizardStep.PUBLISH) {
        return;
      }

      setWizardStep(nextStep);
      setDetailsForSchedulingIfNeeded();
    }
  };

  const getKeyForPublishParams = (accountId: SocialMediaAccountId): string => {
    return `publish-params-${accountId}`;
  };

  const scrollToAccountSection = (accountId: SocialMediaAccountId | null): void => {
    if (!accountId) {
      return;
    }

    const publishParamHandlerClass = PUBLISH_PARAM_HANDLER_CLASS;
    const yOffset = -100;
    const element = document.querySelectorAll(`.${publishParamHandlerClass}[data-key="${getKeyForPublishParams(accountId)}"]`)[0];
    const y = element.getBoundingClientRect().top + yOffset + window.scrollY;

    window.scrollTo({top: y, behavior: 'smooth'});
  };

  const initPanelSpecificHandlersOnCurrentStep = (currentStep: SocialMediaWizardStep): void => {
    const captionErrorAccount = getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors);
    const titleErrorAccount = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors);
    const isAnyAccountErrorProne = captionErrorAccount !== null || titleErrorAccount !== null;

    if (currentStep === SocialMediaWizardStep.PUBLISH && isAnyAccountErrorProne) {
      dispatch(updatePublishingAction(undefined));
      scrollToAccountSection(captionErrorAccount ?? titleErrorAccount);
      return;
    }

    if (currentStep === SocialMediaWizardStep.ADD_DETAILS) {
      dispatch(initPublishingParams());
      dispatch(syncPublishingParams());
      void dispatch(
        savePreferredProfileSelectionUser({
          socialMediaAccountIds: accountIdsToPublishTo,
          idPost: wizardPost?.id,
        })
      );
    }

    if (currentStep === SocialMediaWizardStep.PUBLISH) {
      if (publishingAction === undefined && !isAnyAccountErrorProne) {
        dispatch(updatePublishingAction(SocialPostPublishingAction.PUBLISH));
      }
    } else {
      dispatch(updatePublishingAction(undefined));
    }
  };

  const doToggleNextButtonVisibility = (): void => {
    dispatch(showNextButton());
  };

  const hideMobilePageSelectorPopup = (): void => {
    document.querySelector('.mobile-pages-view')?.classList.add('_hidden');
  };

  const initPanelSpecificHandlersOnNext = (): void => {
    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      setBottomShadowSelector(false);
      const designs = designsToPublish;
      dispatch(showNextButton());

      if (designs.length > 0) {
        dispatch(updateDesignsToPublish(designs));
      }
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS) {
      dispatch(resetAccountResizeStatusForDesignsMap());
    }
  };

  const resetSocialPostCoverInPublishingParamsIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    const isPreviouslySelectedPostSingleGraphic = designsToPublishTo.length === 1;
    const shouldResetCoverForReselectedDesign = newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isPreviouslySelectedPostSingleGraphic;

    if (shouldResetCoverForReselectedDesign) {
      dispatch(resetSocialPostCoverForAllPublishingParams());
    }
  };

  const cleanupTemplateFlowIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    if (newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isTemplateSelected) {
      setDesignsToPublish([]);
      dispatch(updateDesignsToPublish([]));
      dispatch(updateIsTemplateSelected(false));
    }
  };

  const onBack: VoidFunction = (): void => {
    const previousStep: SocialMediaWizardStep | undefined = previousSteps.pop();

    if (previousStep) {
      prevStepRef.current = null;
      resetSocialPostCoverInPublishingParamsIfNeeded(previousStep);
      cleanupTemplateFlowIfNeeded(previousStep);
      setWizardStep(previousStep);
    } else {
      if (isEventsSocialPostingFlow()) {
        goBackToEvent();
        return;
      }

      if (isEventOneClickPromotionFlow()) {
        goBackToEventFromOneClickPromotion();
        return;
      }

      if (!document.referrer || window.PMW.isReferrer()) {
        window.location.href = window.PMW.util.site_url('posters/mine#/posts');
        return;
      }
      window.location.href = document.referrer;
    }
  };

  const onDesignListChange = (designsToPublish: DesignSelection[]): void => {
    if (designsToPublish.length === 0) {
      dispatch(disableNextButton());
    } else {
      dispatch(enableNextButton());
    }

    setDesignsToPublish(designsToPublish);
  };

  useEffect((): VoidFunction => {
    const previewContainerDiv = previewContainerRef;
    const designSelectorDiv = designSelectorRef;

    if (previewContainerDiv.current && designSelectorDiv.current) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === previewContainerDiv.current && entry.isIntersecting) {
            setBottomShadowSelector(false);
          } else {
            setBottomShadowSelector(true);
          }
        });
      });

      observer.observe(previewContainerDiv.current);
      observer.observe(designSelectorDiv.current);

      return () => {
        observer.disconnect();
      };
    }

    return noop;
  }, [designSelectorRef.current, previewContainerRef.current]);

  const getDesignSelectorPreviewClasses = (): string => {
    const mobileViewClasses = isMobileView() ? styles.carouselClasses : ``;

    if (bottomShadowSelector) {
      return `${mobileViewClasses} ${styles.bottomShadow}`;
    }

    return mobileViewClasses;
  };

  const shouldShowDesignSelectorPreview = (): boolean => {
    return designsToPublish.length !== 0 && designSelectorTab !== DesignSelectorTab.GALLERY_TEMPLATES;
  };

  const onGalleryTemplatesSelected = (templateDesigns: DesignSelection[]): void => {
    dispatch(updateIsTemplateSelected(true));
    setDesignsToPublish(templateDesigns);

    dispatch(updateDesignsToPublishAndTriggerNext(templateDesigns));
  };

  const getDesignSelectorPreview = (): ReactElement<any> | null => {
    if (!shouldShowDesignSelectorPreview()) {
      return null;
    }

    return (
      <SocialMediaWizardDesignSelectorPreview
        containerClasses={getDesignSelectorPreviewClasses()}
        onUpdateDesignsToPublishTo={onDesignListChange}
        designsToPublishTo={designsToPublish}
      />
    );
  };

  const getSelectorContainerIfNeeded = (): ReactElement<any> | null => {
    if (designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      return null;
    }

    return <div ref={previewContainerRef} className={`${styles.selectorContainer} ${isMobileView() ? styles.mobileView : ''}`} />;
  };

  const onTabSelected = (newTab: DesignSelectorTab): void => {
    if (newTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      setDesignsToPublish([]);
    }

    dispatch(updateDesignSelectorTab(newTab));
  };

  const getFilteredDesigns = (designs: DesignSelection[]): DesignSelection[] => {
    const sanitizedArray = designs.filter((design) => design.posterHashedId && design.pageHashedId);
    if (sanitizedArray.length > MAX_UPLOADS_TO_DESIGNS) {
      sanitizedArray.splice(MAX_UPLOADS_TO_DESIGNS);
    }

    return sanitizedArray;
  };

  onUploadMediaDesignsCallbackRef.current = (designs: DesignSelection[]): void => {
    const mergedDesigns = [...designsToPublish, ...getFilteredDesigns(designs)];
    setDesignsToPublish(mergedDesigns);
    setDesignSelectorIsLoading(false);
    updateTriggerDelayedNext();
  };

  /**
   * Needed because useState has no callbacks availables similar to this.setState(..., [callback]);
   */
  const updateTriggerDelayedNext = (): void => {
    setTimeout((): void => {
      dispatch(updateTriggerNext());
    }, 100);
  };

  const getStartDesignFromUploadBtnOpts = (): CreateDesignFromMediaModalOpts => {
    return {
      allowMultipagePosterCreation: false,
      maxSelectionNumber: MAX_UPLOADS_TO_DESIGNS,
      shouldUseFastPendingImageUploadFlow: false,
      onAllPostersCreated: (posterHashedIds: PosterHashedId[]): void => {
        getPostersPageIds(posterHashedIds)
          .then((pageIdsMap: Record<PosterHashedId, PageHashedId[]>) => {
            const designSelections = Object.keys(pageIdsMap).flatMap((posterId) => {
              return pageIdsMap[posterId].map((pageId): DesignSelection => {
                return {posterHashedId: posterId, pageHashedId: pageId};
              });
            });

            onUploadMediaDesignsCallbackRef.current(designSelections);
          })
          .catch((err: unknown) => {
            console.error('Error fetching poster page IDs:', err);
          });
      },
      onPosterCreated: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CREATED);
      },
      onTriggered: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CLICKED);
      },
      onPosterCreationsStarted: (): void => {
        setDesignSelectorIsLoading(true);
        dispatch(disableNextButton());
      },
    };
  };

  const getDesignGalleryHeightForSelectDesignStep = (): string => {
    const designGalleryBottomOffset = 250;
    return `${window.innerHeight - designGalleryBottomOffset}px`;
  };

  const getDesignSelectorComponent = (): ReactElement<any> => {
    return (
      <div>
        <DesignSelector
          containerRef={designSelectorRef}
          initialDesignSelections={designsToPublish}
          onGalleryTemplateSelectionCallback={onGalleryTemplatesSelected}
          onDesignsSelection={onDesignListChange}
          designSelectorTab={designSelectorTab}
          onTabSelected={onTabSelected}
          startDesignFromMediaModalBtnOpts={getStartDesignFromUploadBtnOpts()}
          isLoading={isDesignSelectorLoading}
          designGalleryStartingURI={getStartingURIForGallery()}
          designGalleryHeight={getDesignGalleryHeightForSelectDesignStep()}
          scrollContainerEl={window}
        />
        {getDesignSelectorPreview()}
        {getSelectorContainerIfNeeded()}
      </div>
    );
  };

  const getDesignSelectorTab = (): DesignSelectorTab => {
    if (isGalleryFlow) {
      return DesignSelectorTab.GALLERY_TEMPLATES;
    }

    return DesignSelectorTab.MY_DESIGNS;
  };

  const setSelectDesignPanel = (): void => {
    if (isSelectTemplateFlow && !isUserLoggedIn()) {
      mainContent = <DesignGallery onDesignsSelected={onGalleryTemplatesSelected} height="83.5vh" startingPageURI={getStartingURIForGallery()} />;
    } else {
      mainContent = getDesignSelectorComponent();
    }

    previewContent = undefined;
    header = window.i18next.t('pmwjs_start_by_selecting_design');
  };

  const waitForFreshPreview = (): void => {
    setIsPreviewLoading(true);
  };

  const onFreshPreviewFetched = (): void => {
    setIsPreviewLoading(false);
  };

  async function onEmbeddedEditorForTemplateClosed(designIdEdited: PosterHashedId): Promise<void> {
    const pageIdsMap = await getPostersPageIds([designIdEdited]);
    const pageId = pageIdsMap[designIdEdited].at(0);

    if (!pageId) {
      return;
    }

    const designEdited: DesignSelection = {posterHashedId: designIdEdited, pageHashedId: pageIdsMap[designIdEdited][0]};

    if (designEdited.posterHashedId !== designsToPublish[0].posterHashedId && designEdited.posterHashedId !== designsToPublish[0].posterHashedId) {
      dispatch(syncEditedDesign(designEdited));
    }

    const {areAllAccountsSupported, hasAnyVideo} = await updatePublishingDesignsMeta(designEdited);
    const url = await getPosterScreenPreviewURL(designEdited.posterHashedId, designEdited.pageHashedId);

    waitForFreshPreview();

    pollForUpdatesInURL(url, hasAnyVideo)
      .promise.then(async (): Promise<void> => {
        onFreshPreviewFetched();
        await updatePublishingDesignsMeta(designEdited);
      })
      .catch((e: unknown) => {
        console.log(e);
      });

    if (areAllAccountsSupported) {
      dispatch(updateTriggerNext());
      dispatch(hideAccountLevelErrorMessage());
    } else {
      dispatch(
        updateAccountLevelErrorMessage({
          title: window.i18next.t('pmwjs_unsupported_account_error_title'),
          description: window.i18next.t('pmwjs_unsupported_account_error_desc'),
        })
      );
      dispatch(updateTriggerPrevious());
    }
  }

  const setEditTemplatePanel = (): void => {
    mainContent = (
      <TemplateEditorPanel
        onSave={(posterHashId) => {
          void onEmbeddedEditorForTemplateClosed(posterHashId);
        }}
        onClose={onCancel}
      />
    );
    header = '';
    previewContent = undefined;
  };

  const onCancel = (): void => {
    dispatch(updateTriggerPrevious());
  };

  const getMainContentForConnectAccountsPanel = (): ReactElement<any> => {
    const numAccountsConnected = connectedAccounts.length;

    if (numAccountsConnected === 0) {
      return <SocialAccountButtons />;
    }

    return (
      <>
        <div className="spacing-m-b-4 flex-row-align-center">
          <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_20} type={IconType.GHOST} shape={IconShape.SQUARE} className="spacing-m-r-2" onClick={onCancel} />
          <Text size={TextSize.SMALL} className="spacing-m-l-2" val={window.i18next.t('pmwjs_add_another_account')} bold />
        </div>
        <SocialAccountButtons />
      </>
    );
  };

  const setConnectAccountsPanel = (): void => {
    mainContent = getMainContentForConnectAccountsPanel();
    header = window.i18next.t('pmwjs_where_to_publish');
    dispatch(disableNextButton());
  };

  const setAccountConnectionPanel = (): void => {
    if (accountTypeToConnect) {
      mainContent = <SocialAccountConnection accountTypeToConnect={accountTypeToConnect} />;
      header = window.i18next.t('pmwjs_where_to_publish');
    }
  };

  const setSelectAccountsPanel = (): void => {
    mainContent = <SocialAccountsList />;
    header = window.i18next.t('pmwjs_one_or_more_accounts');
  };

  const onClickAddAccount = (): void => {
    setWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS);
  };

  const getSocialWizardPublishingPanelHeading = (): string => {
    let publishingHeader = window.i18next.t('pmwjs_publishing');

    if (publishingAction) {
      if (haveAllPostsPublished) {
        publishingHeader = getLoadedTextByPublishingAction(publishingAction);
      } else {
        publishingHeader = getLoadingTextByPublishingAction(publishingAction);
      }
    }

    return publishingHeader;
  };

  const setPublishingPanel = (): void => {
    if (accountIdsToPublishTo.length > 0) {
      header = getSocialWizardPublishingPanelHeading();
      mainContent = <SocialMediaPostDetails onClickAddAccount={onClickAddAccount} size={getWizardContentSize(params)} key={'publishing-panel'} />;
      previewContent = undefined;
    }
  };

  const setAddDetailsPanel = (): void => {
    if (accountIdsToPublishTo.length > 1) {
      header = window.i18next.t('pmwjs_add_details_to_your_posts_x', {x: accountIdsToPublishTo.length});
    } else {
      header = window.i18next.t('pmwjs_add_details_to_your_post');
    }
    mainContent = <SocialMediaPostDetails onClickAddAccount={onClickAddAccount} size={getWizardContentSize(params)} key={'details-panel'} />;
    previewContent = undefined;
  };

  const isInvalidStepCombination = (): boolean => {
    return (prevStepRef.current === SocialMediaWizardStep.SELECT_DESIGN || wizardStep === SocialMediaWizardStep.SELECT_DESIGN) && post !== null;
  };

  const populateHistoryStack = (): void => {
    if (wizardStep && prevStepRef.current && !isInvalidStepCombination()) {
      const updatedPreviousStep = previousSteps;
      updatedPreviousStep.push(prevStepRef.current);
      setPreviousSteps(updatedPreviousStep);
    }

    prevStepRef.current = wizardStep;
  };

  const onPopStateChange = (popStateEvent: PopStateEventWithStep): void => {
    if (typeof popStateEvent.state === 'object') {
      const {step} = popStateEvent.state;

      if (step) {
        dispatch(updateWizardStep(step));
        setIsNavigatingBack(true);
      }
    }
  };

  const updateWindowHistoryState = (step: SocialMediaWizardStep): void => {
    if (!isNavigatingBack) {
      window.history.pushState({step}, '', '');
    }
  };

  useUpdatePublishingParams();

  useDidUpdateEffect(() => {
    populateHistoryStack();
    doUpdateNextButtonIfNeeded(wizardStep);
  }, [wizardStep]);

  useEffect(() => {
    doToggleNextButtonVisibility();

    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      dispatch(updateDesignSelectorTab(getDesignSelectorTab()));

      if (designsToPublish.length === 0) {
        dispatch(disableNextButton());
      }

      setDesignsToPublish(designsToPublishTo);
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS) {
      setIsPreviewLoading(true);
      void checkUpdateInGeneratedPreviewsStatus(designsToPublishTo);
    }

    // codeReviewAhsan: review this check if needed
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      if (isEventsSocialPostingFlow()) {
        linkSocialPostToEvent();
      }
    }

    updateWindowHistoryState(wizardStep);
  }, [wizardStep]);

  const checkUpdateInGeneratedPreviewsStatus = async (designs: DesignSelection[]): Promise<void> => {
    if (!isUserLoggedIn()) {
      return;
    }

    const pollingHaveAllUpdatedPreviewsBeenGenerated = async (): Promise<void> => {
      return (await window.PMW.writeLocal('poster/haveAllUpdatedPreviewsBeenGenerated', {designs: JSON.stringify(designs)})) as Promise<void>;
    };

    try {
      const POLLING_REQUEST_TIMEOUT = 600000;

      const allGenerated = (await window.PMW.pollingAjaxCallAsync(asyncnoop, pollingHaveAllUpdatedPreviewsBeenGenerated, POLLING_REQUEST_TIMEOUT)) as AjaxSuccessResponse;

      if (allGenerated) {
        setAreAllPreviewsUpToDate(true);
        return;
      }

      openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_poster_previews')});
    } catch (e) {
      openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_posters')});
    }
    return;
  };

  const fetchGraphicsMetaForAnonymousTemplates = async (designToFetch: DesignSelection): Promise<void> => {
    const graphicMeta = await getGraphicMeta(designToFetch);

    const graphicsMetaMap: GraphicsMetaMapType = {};
    graphicsMetaMap[designToFetch.posterHashedId] ??= {};
    graphicsMetaMap[designToFetch.posterHashedId][designToFetch.pageHashedId] = graphicMeta;

    dispatch(updateDesignsGraphicMetaMap(graphicsMetaMap));
  };

  const toggleNextButtonByAccountsConnected = (): void => {
    if (connectedAccounts.length === 0) {
      dispatch(disableNextButton());
    } else if (accountIdsToPublishTo.length !== 0) {
      dispatch(enableNextButton());
    }
  };

  const getSanitizedEmphasizedPlatform = (): SocialPlatform | undefined => {
    return emphasizedPlatform ?? undefined;
  };

  useEffect(() => {
    toggleNextButtonByAccountsConnected();
  }, [connectedAccounts]);

  const handleExistingDesignFromURL = (designToUpdate: DesignSelection): void => {
    dispatch(updateDesignsToPublish([designToUpdate]));
    setDesignsToPublish([designToUpdate]);
  };

  useEffect(() => {
    setIsNavigatingBack(false);
    window.addEventListener('popstate', onPopStateChange);
    setWizardStep(
      getNextWizardStep(post, accounts, null, {
        isGalleryFlow: isSelectTemplateFlow,
        template,
        emphasizedPlatform,
      })
    );
    dispatch(updateHasSeenOnboarding(hasSeenOnboarding));
    dispatch(updateHasSeenConfetti(hasSeenConfetti));
    dispatch(updateHasUserPublishedBefore(hasUserPublishedBefore));
    dispatch(updateIsSocialMediaWizardAiCaptionsEnabled(isSocialMediaWizardAiCaptionsEnabled));
    dispatch(updateIsGoogleBusinessProfileEnabled(isGoogleBusinessProfileEnabled));
    dispatch(updateNumParentAccountsConnected(numParentAccountsConnected));

    const sanitizedPlatformToConnect = getSanitizedEmphasizedPlatform();

    if (sanitizedPlatformToConnect) {
      dispatch(updateEmphasizedPlatform(sanitizedPlatformToConnect));
      dispatch(updateAccountTypeToConnect(sanitizedPlatformToConnect));
    }

    initPanelSpecificHandlersOnCurrentStep(wizardStep);
    toggleNextButtonByAccountsConnected();

    if (accountIdsForPost.length > 0) {
      dispatch(updateAccountIdsToPublishTo(accountIdsForPost));
    }

    if (design) {
      dispatch(updateDesignsToPublish([design]));
    }

    if (isGalleryFlow) {
      dispatch(updateIsTemplateSelected(true));
    }

    if (template) {
      dispatch(updateIsTemplateSelected(true));
      handleExistingDesignFromURL(template);
    }

    if (post) {
      const {publishingParams} = post;
      if (publishingParams) {
        dispatch(updatePublishingParams(publishingParams));
      }

      dispatch(updatePost(post));
    }

    return (): void => {
      window.removeEventListener('popstate', onPopStateChange);
    };
  }, []);

  useDidUpdateEffect(() => {
    onNext();
  }, [triggerNext]);

  useDidUpdateEffect(() => {
    onBack();
  }, [triggerPrevious]);

  const setFluidContentIfNeeded = (): void => {
    isFluidContent = wizardStep === SocialMediaWizardStep.SELECT_DESIGN;
  };

  if (wizardStep) {
    setFluidContentIfNeeded();
    switch (wizardStep) {
      case SocialMediaWizardStep.SELECT_DESIGN:
        setSelectDesignPanel();

        break;
      case SocialMediaWizardStep.CONNECT_ACCOUNTS:
        setConnectAccountsPanel();

        break;
      case SocialMediaWizardStep.ACCOUNT_CONNECTION:
        if (!accountTypeToConnect) {
          openErrorModal({message: window.i18next.t('pmwjs_error_message')});
        }
        setAccountConnectionPanel();

        break;
      case SocialMediaWizardStep.SELECT_ACCOUNTS:
        setSelectAccountsPanel();

        break;
      case SocialMediaWizardStep.EDIT_TEMPLATE:
        setEditTemplatePanel();

        break;
      case SocialMediaWizardStep.ADD_DETAILS:
        setAddDetailsPanel();

        break;
      case SocialMediaWizardStep.PUBLISH:
        setPublishingPanel();
        break;
    }
  }

  const getNavSteps = (): string[] => {
    const steps: string[] = [];

    if (!post && !template) {
      steps.push(window.i18next.t('pmwjs_design'));
    }

    steps.push(window.i18next.t('pmwjs_accounts'));

    if (isSelectTemplateFlow) {
      steps.push(window.i18next.t('pmwjs_edit'));
    }

    steps.push(window.i18next.t('pmwjs_publish'));

    return steps;
  };

  const getFooter = (): ReactElement<any> | null => {
    const size = getWizardContentSize(params);
    const numberSteps = getNavSteps().length;

    if (wizardStep === SocialMediaWizardStep.PUBLISH) {
      return null;
    }

    if (size === SocialMediaWizardContentSize.XSMALL || size === SocialMediaWizardContentSize.SMALL) {
      return (
        <SocialMediaMobileFooter
          shouldShowShadow={bottomShadow && !isLoadingPreview()}
          onNext={onNext}
          onPrevious={onBack}
          nSteps={numberSteps}
          currentStep={previousSteps.length + 1}
          currentSelectedDesigns={designsToPublish}
        />
      );
    }

    return null;
  };

  const getContainerShadowClasses = (): string => {
    if (topShadow) {
      return styles.topShadow;
    }

    return '';
  };

  const isLoadingPreview = (): boolean => {
    return wizardStep !== SocialMediaWizardStep.PUBLISH && wizardStep !== SocialMediaWizardStep.ADD_DETAILS && navView === WizardMobileNavView.PREVIEW;
  };

  const getContainerClasses = (): string => {
    const size = getWizardContentSize(params);
    const isMobile = size === SocialMediaWizardContentSize.XSMALL || size === SocialMediaWizardContentSize.SMALL;
    const isLoading = isLoadingPreview();
    const numberAccountsToPublishTo = accountIdsToPublishTo.length;

    if (isMobile) {
      return `${!isLoading && numberAccountsToPublishTo > 1 ? styles.overflowClasses : '_full-height'} ${getContainerShadowClasses()} _full-width spacing-m-b-10`;
    }

    return `_full-width`;
  };

  const socialMediaWizardContainerQueriesContextValues = useMemo(() => {
    return {
      isExtraSmallScreen: containerQueryParamProps.isExtraSmallScreen,
      isSmallScreen: containerQueryParamProps.isSmallScreen,
      isMediumScreen: containerQueryParamProps.isMediumScreen,
      isLargeScreen: containerQueryParamProps.isLargeScreen,
      isExtraLargeScreen: containerQueryParamProps.isExtraLargeScreen,
    };
  }, [containerQueryParamProps]);

  return (
    <>
      <SocialMediaWizardNav currentSelectedDesigns={designsToPublish} navSteps={getNavSteps()} onNext={onNext} onBack={onBack} />
      <SocialMediaWizardContainerQueriesContext.Provider value={socialMediaWizardContainerQueriesContextValues}>
        <div ref={containerRef} className={getContainerClasses()}>
          {hasContainerQueryInitialized ? (
            <>
              <SocialMediaWizardContent
                size={getWizardContentSize(params)}
                heading={header}
                mainContent={mainContent}
                previewContent={previewContent}
                fluidWidth={isFluidContent}
              />
              <SocialMediaWizardOnboarding />
            </>
          ) : (
            <CircularProgressLoader classes="-for-mystuff-content" />
          )}
        </div>
      </SocialMediaWizardContainerQueriesContext.Provider>
      {getFooter()}
    </>
  );
}
